/**
 * Sticky add-to-cart (mobile)
 * ----------------------------------------------------------------------------
 * Pins the real product form to the bottom of the screen whenever the
 * add-to-cart button is out of view, and lets it return to its normal place in
 * the page when it is visible again.
 *
 * Instead of tracking scroll offsets manually, we observe a zero-height
 * sentinel placed at the original position of the buy buttons. Because the
 * sentinel never moves (a placeholder reserves the form's space while it is
 * pinned), the IntersectionObserver toggles cleanly without flickering.
 *
 * Only the inner <buy-buttons> element is swapped out on a variant change, so
 * the <product-form>, the sentinel and the placeholder all survive partial
 * re-renders. A full product change (connected products) re-initialises.
 */
(function () {
  "use strict";

  var MOBILE_QUERY = "(max-width: 900px)";
  var ACTIVE_CLASS = "sticky-atc--active";
  var teardown = null;

  function setup() {
    if (teardown) {
      teardown();
    }

    var form = document.querySelector(".product-info__block-list product-form form");

    if (!form) {
      return;
    }

    var productForm = form.closest("product-form") || form;

    var sentinel = document.createElement("div");
    sentinel.className = "sticky-atc__sentinel";
    sentinel.setAttribute("aria-hidden", "true");
    productForm.parentNode.insertBefore(sentinel, productForm);

    var placeholder = document.createElement("div");
    placeholder.className = "sticky-atc__placeholder";
    placeholder.setAttribute("aria-hidden", "true");
    productForm.parentNode.insertBefore(placeholder, productForm.nextSibling);

    var mediaQuery = window.matchMedia(MOBILE_QUERY);
    var observer = null;
    var isActive = false;

    function setActive(state) {
      if (state === isActive) {
        return;
      }

      isActive = state;

      if (state) {
        placeholder.style.height = form.offsetHeight + "px";
        form.classList.add(ACTIVE_CLASS);
      } else {
        form.classList.remove(ACTIVE_CLASS);
        placeholder.style.height = "";
      }
    }

    function onIntersect(entries) {
      // Show the sticky bar whenever the buy buttons are not in view.
      setActive(!entries[0].isIntersecting);
    }

    function enable() {
      if (observer) {
        return;
      }

      observer = new IntersectionObserver(onIntersect, { threshold: 0 });
      observer.observe(sentinel);
    }

    function disable() {
      if (observer) {
        observer.disconnect();
        observer = null;
      }

      setActive(false);
    }

    function onMediaChange(event) {
      if (event.matches) {
        enable();
      } else {
        disable();
      }
    }

    // Keep the reserved space accurate if the button height changes (e.g. a
    // variant becomes sold out) while the form is pinned.
    function onRerender() {
      if (isActive) {
        placeholder.style.height = form.offsetHeight + "px";
      }
    }

    onMediaChange(mediaQuery);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", onMediaChange);
    } else {
      mediaQuery.addListener(onMediaChange);
    }

    form.addEventListener("product:rerender", onRerender);

    teardown = function () {
      disable();

      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", onMediaChange);
      } else {
        mediaQuery.removeListener(onMediaChange);
      }

      form.removeEventListener("product:rerender", onRerender);

      if (sentinel.parentNode) {
        sentinel.parentNode.removeChild(sentinel);
      }

      if (placeholder.parentNode) {
        placeholder.parentNode.removeChild(placeholder);
      }

      teardown = null;
    };
  }

  function boot() {
    setup();

    // Re-initialise after a full product re-render (e.g. connected product switch).
    document.addEventListener("product:rerender", function (event) {
      if (event.detail && event.detail.productChange) {
        window.setTimeout(setup, 50);
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
